import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { BiHeadphone } from "react-icons/bi";
import { BsPerson, BsEnvelope, BsTelephone } from "react-icons/bs";
import HomeCTA from "../HomeCTA/HomeCTA";
import "./ServiceDetail.css";

// Banner Image
import defaultBannerImg from "../../assets/ServiceDetail/service_banner.webp";
import financeImg from "../../assets/Home/Service/comp4.png";
import techImg from "../../assets/Home/Service/comp2.png";
import epcImg from "../../assets/Home/Service/comp3.png";
import bessImg from "../../assets/Home/Service/comp1.png";

const serviceDataList = {
  "residential-solar-installation": {
    slug: "residential-solar-installation",
    title: "Residential Solar Installation",
    tagline:
      "Automate routine tasks to free your time, so you can focus on building relationships, closing deals, and celebrating wins that drive consistent sales success.",
    bannerImage: defaultBannerImg,
    stats: [
      { value: "10+", label: "Years Experience" },
      { value: "5000+", label: "Successful Installations" },
      { value: "98%", label: "Customer Satisfaction Rate" },
    ],
    overview: [
      "We Deliver Reliable And Cost-Effective Solar Energy Solutions Designed To Meet The Growing Energy Needs Of Homes And Businesses. Our Services Combine Advanced Solar Technology With Expert Engineering To Provide Clean, Renewable Power That Reduces Electricity Costs And Supports A Sustainable Future.",
      "From Initial Consultation And System Design To Installation And Long-Term Maintenance, We Manage The Entire Solar Journey With Precision And Care. Every Solution Is Customized To Maximize Energy Efficiency, Performance, And Long-Term Savings.",
      "By Choosing Our Solar Services, You Gain Energy Independence, Lower Operational Expenses, And A Dependable Power Source Backed By Professional Support And Proven Expertise.",
    ],
    features: [
      {
        title: "Customized Solar Solutions",
        desc: "Every Solar System Is Designed Based On Your Energy Needs, Location, And Usage Patterns. We Ensure Optimal Panel Placement And System Sizing To Deliver Maximum Efficiency And Long-Term Performance.",
      },
      {
        title: "High-Quality Components",
        desc: "We Use Premium-Grade Solar Panels, Inverters, And Mounting Structures From Trusted Manufacturers. This Guarantees Durability, Safety, And Reliable Energy Generation For Years To Come.",
      },
      {
        title: "Expert Installation & Engineering",
        desc: "Our Certified Technicians Handle The Entire Installation Process With Precision And Care. From Electrical Connections To System Testing, We Follow Industry Standards To Ensure Seamless And Secure Operation.",
      },
      {
        title: "Ongoing Support & Maintenance",
        desc: "We Provide Continuous Monitoring, Regular Maintenance, And Prompt Technical Support To Keep Your Solar System Performing At Its Best. Our Team Is Always Available To Assist You Whenever Needed.",
      },
    ],
    whyChoose: [
      {
        id: "01/4",
        title: "Cost-Effective & Long-Term Savings",
        desc: "By choosing solar, you reduce carbon emissions and dependence on fossil fuels. Our services support a cleaner environment while helping you contribute to a sustainable future.",
      },
      {
        id: "02/4",
        title: "Sustainable & Eco-Friendly Power",
        desc: "Harness continuous clean solar energy generated on-site, diminishing environmental carbon footprints while ensuring stable energy pricing against utility rate hikes.",
      },
      {
        id: "03/4",
        title: "End-to-End Service Management",
        desc: "From initial feasibility analysis, permitting, engineering designs through full turnkey execution and grid interconnection, we handle every milestone.",
      },
      {
        id: "04/4",
        title: "Reliable Support & Maintenance",
        desc: "Round-the-clock monitoring and dedicated technical field support guarantee high uptime, optimized generation yield, and swift resolution.",
      },
    ],
  },
  "finance": {
    slug: "finance",
    title: "Financial Consultancy & Advisory",
    tagline:
      "Strategic financial modeling, project finance, debt syndication, and financial due diligence for high-impact clean energy developments.",
    bannerImage: financeImg,
    stats: [
      { value: "$1B+", label: "Transactions Supported" },
      { value: "45+", label: "Financing Projects Closed" },
      { value: "99%", label: "Advisory Success Rate" },
    ],
    overview: [
      "We provide institutional-grade financial advisory services dedicated to the renewable energy sector, assisting sponsors, developers, and investment funds worldwide.",
      "Our team crafts bankable financial models, evaluates capital structures, negotiates power purchase agreements (PPAs), and secures favorable project finance terms.",
      "With deep sector experience, we mitigate financial uncertainties, optimize equity returns, and accelerate financial close.",
    ],
    features: [
      {
        title: "Project Finance & Capital Structuring",
        desc: "Tailored debt and equity structures designed to optimize weighted average cost of capital (WACC) and maximize project internal rate of return (IRR).",
      },
      {
        title: "Financial Due Diligence & Audits",
        desc: "Rigorous valuation, cash flow sensitivity modeling, tax equity structuring, and risk reviews for lenders and investors.",
      },
      {
        title: "PPA Advisory & Tariff Structuring",
        desc: "Negotiating long-term off-take agreements, corporate PPAs, and merchant risk mitigation frameworks.",
      },
      {
        title: "Refinancing & Asset Optimization",
        desc: "Restructuring existing operational portfolio debts to capture lower interest rates and release trapped liquidity.",
      },
    ],
    whyChoose: [
      {
        id: "01/4",
        title: "Proven Transaction Track Record",
        desc: "Over $1B+ in clean energy deals advised across solar, wind, and storage assets globally.",
      },
      {
        id: "02/4",
        title: "Institutional Lender Network",
        desc: "Direct relationships with top infrastructure funds, development banks, and commercial lenders.",
      },
      {
        id: "03/4",
        title: "Transparent Risk Modeling",
        desc: "Advanced multi-variable sensitivity forecasting that builds confidence with institutional credit committees.",
      },
      {
        id: "04/4",
        title: "Long-Term Value Creation",
        desc: "Ongoing portfolio refinancing and financial asset management to maximize life-of-plant value.",
      },
    ],
  },
  "project-development": {
    slug: "project-development",
    title: "Technical Consultancy & Engineering",
    tagline:
      "Precision engineering, solar resource assessments, grid connection feasibility, and detailed system design for utility and commercial scale projects.",
    bannerImage: techImg,
    stats: [
      { value: "40+", label: "Engineering Mandates" },
      { value: "1.2GW", label: "Capacity Engineered" },
      { value: "100%", label: "Grid Compliance" },
    ],
    overview: [
      "Our engineering team brings decades of technical expertise across solar PV layout optimization, electrical single line diagrams, and civil structural analysis.",
      "We perform high-resolution shadow analysis, yield projections using PVsyst, and grid compliance assessments to guarantee bankability.",
      "From pre-feasibility through owner's engineering during construction, we protect technical integrity at every phase.",
    ],
    features: [
      {
        title: "Resource & Yield Assessment",
        desc: "Bankable PVsyst energy simulations, albedo modeling, and bifacial gain calculations.",
      },
      {
        title: "Grid Interconnection Studies",
        desc: "Load flow, short-circuit, and dynamic stability studies conforming to regional transmission operator rules.",
      },
      {
        title: "Detailed Engineering & BoP",
        desc: "Complete electrical, civil, and mechanical balance of plant (BoP) package design.",
      },
      {
        title: "Owner's Engineer Supervision",
        desc: "Independent technical oversight ensuring contractors adhere to design specifications and safety codes.",
      },
    ],
    whyChoose: [
      {
        id: "01/4",
        title: "Optimized Plant Performance",
        desc: "Precision engineering maximizes generation yield and reduces levelized cost of electricity (LCOE).",
      },
      {
        id: "02/4",
        title: "Industry Standard Compliance",
        desc: "Fully compliant with IEEE, IEC, and local grid code requirements.",
      },
      {
        id: "03/4",
        title: "Experienced Team of Engineers",
        desc: "Dedicated power system and structural engineers with utility-scale delivery experience.",
      },
      {
        id: "04/4",
        title: "Full Lifecycle Engineering",
        desc: "Comprehensive engineering support from site screening through commissioning testing.",
      },
    ],
  },
  "epc-coordination": {
    slug: "epc-coordination",
    title: "Turnkey EPC & Project Execution",
    tagline:
      "End-to-end EPC execution delivering procurement excellence, on-time construction, and rigorous commissioning for clean energy infrastructure.",
    bannerImage: epcImg,
    stats: [
      { value: "15+", label: "Turnkey Projects Built" },
      { value: "99.8%", label: "On-Time Completion" },
      { value: "0", label: "Lost Time Incidents" },
    ],
    overview: [
      "We deliver full turnkey Engineering, Procurement, and Construction (EPC) services with strict adherence to safety, quality, and budget benchmarks.",
      "Our supply chain network secures Tier-1 solar modules, high-efficiency inverters, and durable tracker systems directly from leading manufacturers.",
      "Experienced site managers oversee civil works, pile driving, electrical integration, and final substation energization.",
    ],
    features: [
      {
        title: "Tier-1 Equipment Procurement",
        desc: "Global supply chain partnerships ensuring highest quality solar panels, inverters, and BESS modules.",
      },
      {
        title: "Civil & Electrical Construction",
        desc: "Site preparation, foundation installation, cable trenching, tracker mounting, and inverter station deployment.",
      },
      {
        title: "Substation & Interconnection",
        desc: "High voltage transformer step-up substations and seamless utility interconnection.",
      },
      {
        title: "Testing & Commissioning",
        desc: "String IV curve tracing, thermal imaging, insulation testing, and performance ratio verification.",
      },
    ],
    whyChoose: [
      {
        id: "01/4",
        title: "Single-Point Accountability",
        desc: "Turnkey responsibility eliminates contractor friction and project timeline delays.",
      },
      {
        id: "02/4",
        title: "Guaranteed Performance Ratios",
        desc: "Comprehensive performance guarantees and warranties backing plant generation.",
      },
      {
        id: "03/4",
        title: "Exemplary Safety Standards",
        desc: "Zero-harm safety culture with rigorous daily inspections and strict OSHA compliance.",
      },
      {
        id: "04/4",
        title: "Fast-Track Deployment",
        desc: "Optimized construction sequencing delivering rapid commercial operation dates (COD).",
      },
    ],
  },
  "emerging": {
    slug: "emerging",
    title: "BESS, Hybrid & Green Hydrogen",
    tagline:
      "Next-generation energy storage, microgrid hybridization, and green hydrogen solutions providing round-the-clock clean baseload power.",
    bannerImage: bessImg,
    stats: [
      { value: "300MWh", label: "Storage Integrated" },
      { value: "24/7", label: "Clean Power Dispatch" },
      { value: "98.5%", label: "Round-Trip Efficiency" },
    ],
    overview: [
      "We engineer and deploy advanced Battery Energy Storage Systems (BESS) and hybrid clean energy power plants for grid resilience and zero-carbon dispatch.",
      "Our solutions combine solar PV, wind, and lithium battery storage managed by intelligent Energy Management Software (EMS).",
      "We also pioneer modular green hydrogen generation systems integrated with dedicated renewable microgrids.",
    ],
    features: [
      {
        title: "Utility-Scale BESS Deployment",
        desc: "Containerized lithium-ion and flow battery systems with advanced thermal management and fire suppression.",
      },
      {
        title: "Hybrid Microgrid Integration",
        desc: "Seamless co-located solar + storage plants providing peak shaving, frequency regulation, and black start capability.",
      },
      {
        title: "Smart Energy Management Systems",
        desc: "AI-driven EMS algorithms optimizing market arbitrage, demand charge reduction, and state-of-charge health.",
      },
      {
        title: "Green Hydrogen Electrolyzer Systems",
        desc: "Proton exchange membrane (PEM) and alkaline electrolyzers powered 100% by renewable generation.",
      },
    ],
    whyChoose: [
      {
        id: "01/4",
        title: "24/7 Dispatchable Clean Energy",
        desc: "Overcome renewable intermittency with rapid-response storage dispatch under 10 milliseconds.",
      },
      {
        id: "02/4",
        title: "Maximum Revenue Stacking",
        desc: "Capture multi-stream grid services including frequency response, capacity markets, and energy arbitrage.",
      },
      {
        id: "03/4",
        title: "Future-Proof Modular Architecture",
        desc: "Scalable containerized architecture ready for future capacity expansion.",
      },
      {
        id: "04/4",
        title: "Full Turnkey Integration",
        desc: "Complete battery rack, inverter, HVAC, and SCADA control delivery.",
      },
    ],
  },
};

// Aliases for quick mapping
const slugAliases = {
  "residential-solar-installation": "residential-solar-installation",
  "solar-installation": "residential-solar-installation",
  "solar": "residential-solar-installation",
  "services": "residential-solar-installation",
  "finance": "finance",
  "financial-consultancy": "finance",
  "project-development": "project-development",
  "technical-consultancy": "project-development",
  "engineering": "project-development",
  "epc-coordination": "epc-coordination",
  "turnkey-epc": "epc-coordination",
  "turnkey-epc-project-execution": "epc-coordination",
  "emerging": "emerging",
  "bess-hybrid": "emerging",
  "bess-hybrid-green-hydrogen": "emerging",
};

// Parse statistic values into prefix, target number, suffix, and decimals count
const parseStatValue = (valStr) => {
  if (typeof valStr !== "string") return { prefix: "", target: 0, suffix: "", decimals: 0 };
  const str = valStr.trim();
  const match = str.match(/^([^\d.]*)(\d+(?:\.\d+)?)(.*)$/);
  if (!match) {
    return { prefix: "", target: 0, suffix: str, decimals: 0 };
  }
  const prefix = match[1] || "";
  const numStr = match[2];
  const suffix = match[3] || "";
  const target = parseFloat(numStr);
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return { prefix, target, suffix, decimals };
};

// Smooth number counting animation matching Sollas design with decimal support and ease-out
const AnimatedStatCounter = ({ rawValue }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -30px 0px" });
  const { prefix, target, suffix, decimals } = parseStatValue(rawValue);
  const [currentVal, setCurrentVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (target === 0) {
      setCurrentVal(0);
      return;
    }

    const duration = 1600; // 1.6s smooth easing animation
    const startTime = performance.now();

    let animId;
    const update = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      // easeOutCubic curve for realistic counter slowdown
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = eased * target;

      if (progress < 1) {
        setCurrentVal(decimals > 0 ? parseFloat(val.toFixed(decimals)) : Math.floor(val));
        animId = requestAnimationFrame(update);
      } else {
        setCurrentVal(target);
      }
    };

    animId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animId);
  }, [inView, target, rawValue, decimals]);

  const formattedNum = decimals > 0 ? currentVal.toFixed(decimals) : currentVal;

  return (
    <span ref={ref} className="stat-big-num">
      {prefix}{formattedNum}{suffix}
    </span>
  );
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [activeAccordion, setActiveAccordion] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Scroll to top when slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const resolvedKey =
    (slug && slugAliases[slug.toLowerCase()]) ||
    (slug && serviceDataList[slug]) ||
    "residential-solar-installation";

  const currentService = serviceDataList[resolvedKey] || serviceDataList["residential-solar-installation"];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (formData.name && (formData.email || formData.phone)) {
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: "", email: "", phone: "" });
      }, 4000);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  return (
    <div className="service-detail-page">
      {/* ====================================================================
          1. HERO HEADER SECTION (Gradient, Title, Tagline, Button)
          ==================================================================== */}
      <section className="service-hero-header-section">
        <div className="container">
          <div className="service-hero-top-row">
            <motion.div
              className="service-hero-title-col"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <h1 className="service-main-heading">{currentService.title}</h1>
            </motion.div>

            <motion.div
              className="service-hero-action-col"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <p className="service-hero-tagline">{currentService.tagline}</p>
              <button
                type="button"
                className="service-get-started-btn"
                onClick={() => {
                  const formEl = document.getElementById("service-call-form");
                  if (formEl) {
                    formEl.scrollIntoView({ behavior: "smooth" });
                  } else {
                    navigate("/contact");
                  }
                }}
              >
                Get Started
              </button>
            </motion.div>
          </div>

          {/* Large Hero Banner Image with Bottom Stats */}
          <motion.div
            className="service-hero-banner-container"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            <div className="service-banner-img-wrap">
              <img
                src={currentService.bannerImage}
                alt={currentService.title}
                className="service-banner-img"
              />
            </div>

            {/* 3 Metrics Statistics Bar with Counting Animation */}
            <div className="service-stats-bar">
              {currentService.stats.map((stat, i) => (
                <div key={`${currentService.slug}-${i}-${stat.value}`} className="service-stat-col">
                  <div className="stat-number-wrap">
                    <AnimatedStatCounter rawValue={stat.value} />
                  </div>
                  <div className="stat-label-wrap">
                    <BiHeadphone className="stat-icon" />
                    <span className="stat-text-label">{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ====================================================================
          2. SERVICE OVERVIEW & KEY FEATURES (Left) + STICKY FORM (Right)
          ==================================================================== */}
      <section className="service-overview-section">
        <div className="container">
          <div className="service-content-grid">
            {/* Left Column: Overview & Features */}
            <motion.div
              className="service-left-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={fadeUp}
            >
              {/* Service Overview Heading & Paragraphs */}
              <div className="service-overview-block">
                <h2 className="section-subheading">Service Overview</h2>
                <div className="overview-paragraphs">
                  {currentService.overview.map((para, i) => (
                    <p key={i} className="overview-text">
                      {para}
                    </p>
                  ))}
                </div>
              </div>

              {/* Key Features Block */}
              <div className="service-features-block">
                <h2 className="section-subheading">Key Features</h2>
                <div className="features-list">
                  {currentService.features.map((feature, i) => (
                    <div key={i} className="feature-item">
                      <h3 className="feature-item-title">{feature.title}</h3>
                      <p className="feature-item-desc">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right Column: Sticky Form */}
            <div className="service-right-sticky-col">
              <div className="service-sticky-card" id="service-call-form">
                <h3 className="form-card-title">Form to receive call</h3>

                {isSubmitted ? (
                  <div className="form-success-message">
                    <div className="success-icon">✓</div>
                    <h4>Thank You!</h4>
                    <p>Your request has been received. Our expert will call you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="receive-call-form">
                    <div className="form-group">
                      <label className="form-label">Name</label>
                      <div className="input-with-icon">
                        <input
                          type="text"
                          name="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="form-control-custom"
                        />
                        <BsPerson className="input-icon" />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <div className="input-with-icon">
                        <input
                          type="email"
                          name="email"
                          placeholder="example@gmail.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-control-custom"
                        />
                        <BsEnvelope className="input-icon" />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <div className="input-with-icon">
                        <input
                          type="tel"
                          name="phone"
                          placeholder="+880 01XXX XXXX"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="form-control-custom"
                        />
                        <BsTelephone className="input-icon" />
                      </div>
                    </div>

                    <button type="submit" className="form-submit-pill-btn">
                      Receive Call
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          3. WHY CHOOSE THIS SERVICE (Interactive Accordion)
          ==================================================================== */}
      <section className="service-why-choose-section">
        <div className="container">
          <motion.div
            className="why-choose-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-30px" }}
            variants={fadeUp}
          >
            <h2 className="why-choose-main-title">Why Choose This Service</h2>
          </motion.div>

          <motion.div 
            className="why-choose-list"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-20px" }}
            variants={fadeUp}
          >
            {currentService.whyChoose.map((item, index) => {
              const isActive = activeAccordion === index;
              return (
                <div
                  key={index}
                  className={`why-choose-card ${isActive ? "active" : "inactive"}`}
                  onClick={() => setActiveAccordion(index)}
                  onMouseEnter={() => setActiveAccordion(index)}
                >
                  <div className="why-choose-row">
                    <div className="why-choose-title-wrap">
                      <span className="why-choose-num">{item.id}</span>
                      <h3 className="why-choose-card-title">{item.title}</h3>
                    </div>

                    <div className={`why-choose-action-d ${isActive ? "visible" : "hidden"}`}>
                      <button
                        type="button"
                        className="why-get-started-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/contact");
                        }}
                      >
                        Get Started
                      </button>
                    </div>
                  </div>

                  <div className={`why-choose-content-wrap ${isActive ? "expanded" : "collapsed"}`}>
                    <p className="why-choose-desc">{item.desc}</p>
                    <div className="why-choose-action-m">
                      <button
                        type="button"
                        className="why-get-started-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/contact");
                        }}
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ====================================================================
          4. HOME CTA SECTION
          ==================================================================== */}
      <div className="service-detail-cta-wrap">
        <HomeCTA />
      </div>
    </div>
  );
};

export default ServiceDetail;
